// deps
import React, { ReactElement, memo, useContext } from "react";

// internal deps
import { PaymentGroupDataDef, SplitPayBannerList, PaymentProgressLoader } from "@swiggy-private/payment-ui";
import { useEffectOnce } from "@swiggy-private/core-ui";

// components
import { PaymentActionProvider } from "@payment-client/components/show-payments/components/payment-action-provider";
import { PaymentGroupHandler } from "@payment-client/components/show-payments/components/payment-group-handler";

// constants
import { options as NetBankingGroupOptions } from "@payment-client/constants/Options";

// hooks
import useSplitPay from "@payment-client/hooks/UseSplitPay";
import { useSwiggyMoneyActions } from "@payment-client/hooks/UseSwiggyMoneyActions";
import useFlipkartCoins from "@payment-client/hooks/useFlipkartCoins";

// contexts
import { ShowPaymentContext } from "@payment-client/contexts/ShowPaymentContext";
import { PaymentPayContext } from "@payment-client/contexts/PaymentPayContext";
import { AnalyticsContext } from "@swiggy-private/checkout-core";
import { PaymentLoaderContext } from "@payment-client/contexts/PaymentLoaderContext";
import { SplitPayListContext } from "@payment-client/contexts/SplitPayListContext";

// helpers
import { getFrictionLayoutId, getPaymentGroup } from "@payment-client/helpers/PaymentHelper";

// analytics
import { morePaymentMethodsImpressionEvent } from "@payment-client/analytics/events/Payment";
import { GetPaymentMethodsResponse } from "@payment-client/interfaces/PaymentApi";

export interface MorePaymentMethodsContentProps {
    paymentGroupName: string;
}

export const MorePaymentMethodsContent = memo(function MorePaymentMethodsContent({
    paymentGroupName,
}: MorePaymentMethodsContentProps): ReactElement {
    const { amountToPay, selectedSplitPayMethods, showSplitPay } = useContext(PaymentPayContext);
    const { paymentResponse } = useContext(ShowPaymentContext);
    const { splitPayList } = useContext(SplitPayListContext);
    const paymentGroup = getPaymentGroup(
        paymentResponse as GetPaymentMethodsResponse,
        paymentGroupName.toUpperCase(),
    ) as PaymentGroupDataDef;
    const { impressionEvent } = useContext(AnalyticsContext);
    const { onSplitPayToggle } = useSplitPay();
    const { addMoneyActions, swiggyMoneyInfoActions } = useSwiggyMoneyActions();
    const onFlipkartToolTipShow = useFlipkartCoins(true);

    const { isLoading } = useContext(PaymentLoaderContext);

    const splitpayList = splitPayList ?? [];
    const isSplitPayBannerVisible = showSplitPay && splitpayList.length > 0;

    // to send impression event
    useEffectOnce(() => {
        const frictionLayoutId = getFrictionLayoutId(paymentResponse);
        morePaymentMethodsImpressionEvent(frictionLayoutId, impressionEvent);
    });

    return (
        <PaymentActionProvider frictionGroupName={paymentGroupName}>
            {({ onMorePaymentsOptionSelect, placeOrderInProgress, ...paymentActions }): ReactElement => (
                <>
                    {isSplitPayBannerVisible && (
                        <SplitPayBannerList
                            selectedSplitpayMethods={selectedSplitPayMethods}
                            splitPayList={splitpayList}
                            handleChange={onSplitPayToggle}
                            addMoneyActions={addMoneyActions}
                            isFrictionLayout={true}
                            swiggyMoneyInfoActions={swiggyMoneyInfoActions}
                            onFlipkartToolTipShow={onFlipkartToolTipShow}
                        />
                    )}
                    <PaymentGroupHandler
                        paymentAmount={String(amountToPay)}
                        paymentGroup={paymentGroup}
                        key={paymentGroup.groupName}
                        paymentInProgress={false}
                        options={NetBankingGroupOptions}
                        {...paymentActions}
                        hideGroupTitle
                        isFrictionPage
                        juspayMeta={paymentResponse?.juspayMeta}
                    />
                    {placeOrderInProgress || isLoading ? <PaymentProgressLoader /> : null}
                </>
            )}
        </PaymentActionProvider>
    );
});
